import { Base } from '../../abstract/base.model';
import { AgremoAnalysisFile } from '../response-models/agremo-analysis-file.model';
import { AgremoAnalysisParams } from '../response-models/agremo-analysis-params.model';
import { AgremoStatistics } from '../response-models/agremo-statistics.model';
import { AgremoAnalysisStatus } from './agremo-analysis-status.model';
import { isRecord } from '../utils/is-record';
import { toAgremoAnalysisFiles } from '../utils/agremo-utils';
import { countOrZero, nullableNumber, nullableString } from '../utils/wire-value-utils';

/**
 * An Agremo analysis of a map, built from the hub's analysis JSON (`user-agremo-controller.ts`
 * `/user/agremo/analyses`, hub `data-models/agremo-analysis.ts`). The detail response adds the field,
 * customer, `boundaryGeojson` and `files` (with pre-signed `downloadUrl`s); the list adds `analysisTypeName`.
 *
 * Analyses are created from a `CreateAnalysisDto` and never updated, so every field is read-only. Fields the
 * hub didn't send stay `undefined` (an explicit `null` stays `null`); display fallbacks belong in templates.
 */
export class AgremoAnalysis extends Base {
  organizationId?: string | null;
  mapId?: string | null;
  agremoAnalysisId?: string | null;
  analysisTypeId?: string | null;
  analysisTypeName?: string | null;
  /** Missing status reads as 'pending', the hub's initial state. */
  status: AgremoAnalysisStatus;
  progress: number;
  requestedAt?: string | null;
  completedAt?: string | null;
  errorMessage?: string | null;
  creditsConsumed?: number | null;
  analysisParams?: AgremoAnalysisParams | null;
  statistics?: AgremoStatistics | null;
  fieldId?: string | null;
  fieldName?: string | null;
  customerId?: string | null;
  customerName?: string | null;
  /** GeoJSON geometry of the field boundary (detail only). Data only. */
  boundaryGeojson?: unknown;
  files: AgremoAnalysisFile[];

  constructor(data: unknown) {
    super(data);
    const source = isRecord(data) ? data : {};
    this.organizationId = nullableString(source['organizationId']);
    this.mapId = nullableString(source['mapId']);
    this.agremoAnalysisId = nullableString(source['agremoAnalysisId']);
    this.analysisTypeId = nullableString(source['analysisTypeId']);
    this.analysisTypeName = nullableString(source['analysisTypeName']);
    this.status = (nullableString(source['status']) ?? 'pending') as AgremoAnalysisStatus;
    this.progress = countOrZero(source['progress']);
    this.requestedAt = nullableString(source['requestedAt']);
    this.completedAt = nullableString(source['completedAt']);
    this.errorMessage = nullableString(source['errorMessage']);
    this.creditsConsumed = nullableNumber(source['creditsConsumed']);
    this.analysisParams = recordOrNull(source['analysisParams']) as AgremoAnalysisParams | null | undefined;
    this.statistics = recordOrNull(source['statistics']) as AgremoStatistics | null | undefined;
    this.fieldId = nullableString(source['fieldId']);
    this.fieldName = nullableString(source['fieldName']);
    this.customerId = nullableString(source['customerId']);
    this.customerName = nullableString(source['customerName']);
    this.boundaryGeojson = source['boundaryGeojson'] ?? undefined;
    this.files = toAgremoAnalysisFiles(source['files']);
  }
}

/** A JSON object block as sent, `null` when the hub sent `null`, otherwise `undefined`. */
function recordOrNull(value: unknown): Record<string, unknown> | null | undefined {
  if (value === null) {
    return null;
  }
  return isRecord(value) ? value : undefined;
}
